import React, { useState, useEffect, useCallback } from "react";
import { HiXMark, HiFire, HiHeart, HiOutlineHeart } from "react-icons/hi2";
import {
  FiClock,
  FiUsers,
  FiEdit3,
  FiTrash2,
  FiPlay,
  FiInfo,
  FiMessageCircle,
} from "react-icons/fi";
import { IoRestaurantOutline, IoTimeOutline } from "react-icons/io5";
import { MdOutlineDescription, MdOutlineVideoLibrary } from "react-icons/md";
import { LuChefHat, LuList } from "react-icons/lu";
import apiConfig from "../../config/api.js";
import { useLanguage } from "../../contexts/LanguageContext";
import { translateTextAPI } from "../../utils/translationAPI";
import { useAutoTranslatedLabels } from "../../hooks/useAutoTranslatedLabels";

/** Libellés en français : l’API les traduit automatiquement selon la langue (plus de clés manuelles) */
const POPUP_LABELS = {
  recipeLabel: "Recette",
  noTitle: "Sans titre",
  closeButton: "Fermer",
  reviews: "avis",
  minutes: "min",
  persShort: "pers.",
  easy: "Facile",
  medium: "Moyen",
  hard: "Difficile",
  favoriteSingular: "Favori",
  favorites: "Favoris",
  edit: "Modifier",
  delete: "Supprimer",
  translating: "Traduction...",
  descriptionLabel: "Description",
  videoLabel: "Vidéo",
  openButton: "Ouvrir",
  ingredients: "Ingrédients",
  stepsLabel: "Étapes",
  infoLabel: "Infos",
  preparation: "Préparation",
  cooking: "Cuisson",
  servings: "Portions",
  difficultyLabel: "Difficulté",
  comments: "Commentaires",
  loadingComments: "Chargement des commentaires...",
  noCommentsYet: "Aucun commentaire pour le moment.",
  anonymous: "Anonyme",
  createdOn: "Créé le",
};

/**
 * Popup détail recette - design propre et élégant
 * Libellés traduits automatiquement via l’API (pas de dictionnaire manuel)
 */
const RecipePopup = ({
  recipe,
  user,
  isOpen,
  onClose,
  onFavoriteToggle,
  onEdit,
  onDelete,
}) => {
  const { language, t } = useLanguage();
  const L = useAutoTranslatedLabels(POPUP_LABELS);
  const [isFavorite, setIsFavorite] = useState(false);
  const [loading, setLoading] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [translatedContent, setTranslatedContent] = useState({
    description: null,
    ingredients: null,
    instructions: null,
    commentTexts: {},
  });
  const [isTranslatingContent, setIsTranslatingContent] = useState(false);

  const translateRecipeContent = useCallback(async () => {
    if (!recipe) return;

    const rawIng = recipe.ingredients
      ? recipe.ingredients.split("\n").filter((l) => l.trim())
      : [];
    const rawInst = recipe.instructions
      ? recipe.instructions.split("\n").filter((l) => l.trim())
      : [];

    // Toujours traduire vers la langue sélectionnée (fr, en, km) pour que description/ingrédients/étapes s'affichent dans la bonne langue
    setIsTranslatingContent(true);
    try {
      const desc = recipe.description
        ? await translateTextAPI(recipe.description, language, "auto")
        : null;
      const translatedIng = await Promise.all(
        rawIng.map((line) => translateTextAPI(line, language, "auto")),
      );
      const translatedInst = await Promise.all(
        rawInst.map((line) => translateTextAPI(line, language, "auto")),
      );
      setTranslatedContent((prev) => ({
        ...prev,
        description: desc ?? recipe.description,
        ingredients: translatedIng.length ? translatedIng : null,
        instructions: translatedInst.length ? translatedInst : null,
      }));
    } catch (err) {
      console.error("Recipe content translation error:", err);
      setTranslatedContent({
        description: recipe?.description ?? null,
        ingredients: rawIng.length ? rawIng : null,
        instructions: rawInst.length ? rawInst : null,
        commentTexts: {},
      });
    } finally {
      setIsTranslatingContent(false);
    }
  }, [recipe, language]);

  useEffect(() => {
    if (!recipe || !isOpen) return;
    setTranslatedContent({
      description: null,
      ingredients: null,
      instructions: null,
      commentTexts: {},
    });
    translateRecipeContent();
  }, [recipe?.id, isOpen, translateRecipeContent]);

  const translateComments = useCallback(async () => {
    if (!comments.length) return;
    setIsTranslatingContent(true);
    try {
      const texts = await Promise.all(
        comments.map((c) => {
          const raw = c.comment_text ?? c.text ?? c.content ?? "";
          return raw
            ? translateTextAPI(raw, language, "auto")
            : Promise.resolve("");
        }),
      );
      const commentTexts = {};
      comments.forEach((c, i) => {
        commentTexts[c.id] =
          texts[i] ?? c.comment_text ?? c.text ?? c.content ?? "";
      });
      setTranslatedContent((prev) => ({ ...prev, commentTexts }));
    } catch (err) {
      console.error("Comments translation error:", err);
    } finally {
      setIsTranslatingContent(false);
    }
  }, [comments, language]);

  useEffect(() => {
    if (!comments.length || language === "en") return;
    translateComments();
  }, [comments, language, translateComments]);

  const checkFavoriteStatus = useCallback(async () => {
    if (!user || !recipe) return;
    try {
      const response = await fetch(
        `http://localhost:3000/api/favorites/check/${recipe.id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );
      if (response.ok) {
        const data = await response.json();
        setIsFavorite(data.isFavorite);
      }
    } catch (error) {
      console.error("Erreur vérification favori:", error);
    }
  }, [user, recipe]);

  useEffect(() => {
    if (user && recipe && isOpen) checkFavoriteStatus();
  }, [user, recipe, isOpen, checkFavoriteStatus]);

  useEffect(() => {
    if (!recipe?.id || !isOpen) return;
    let cancelled = false;
    setCommentsLoading(true);
    fetch(
      `${apiConfig.baseURL}/comments/recipe/${recipe.id}`,
      localStorage.getItem("token")
        ? {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        : {},
    )
      .then((res) => (res.ok ? res.json() : { data: [] }))
      .then((data) => {
        if (!cancelled)
          setComments(
            Array.isArray(data?.comments) ? data.comments : data?.data || [],
          );
      })
      .catch(() => {
        if (!cancelled) setComments([]);
      })
      .finally(() => {
        if (!cancelled) setCommentsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [recipe?.id, isOpen]);

  const handleToggleFavorite = async (e) => {
    e.stopPropagation();
    if (!user) {
      alert(t("mustBeLoggedIn"));
      return;
    }
    setLoading(true);
    try {
      const url = isFavorite
        ? `http://localhost:3000/api/favorites/remove/${recipe.id}`
        : `http://localhost:3000/api/favorites/add/${recipe.id}`;
      const response = await fetch(url, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (response.ok) {
        setIsFavorite(!isFavorite);
        if (onFavoriteToggle) onFavoriteToggle(recipe.id, !isFavorite);
      } else {
        const err = await response.json();
        alert(err.message || t("errorManagingFavorites"));
      }
    } catch (error) {
      alert(t("connectionError"));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleEdit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onClose(); // Fermer d'abord le popup de détails
    if (onEdit) {
      onEdit(recipe); // Puis ouvrir le popup d'édition (pas de navigation)
    }
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (window.confirm(t("confirmDelete"))) {
      onDelete(recipe.id);
      onClose();
    }
  };

  if (!isOpen || !recipe) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={handleClose}
    >
      <div
        className="w-full max-w-5xl max-h-[90vh] flex flex-col bg-white rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header : image + titre + fermer */}
        <div className="relative flex-shrink-0 w-full">
          <div className="w-full aspect-[16/9] sm:h-56 bg-slate-100 overflow-hidden flex">
            {recipe.image_url ? (
              <img
                src={recipe.image_url}
                alt={recipe.title || L.recipeLabel}
                className="w-full min-w-full h-full min-h-full object-cover object-center block"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-slate-100">
                <IoRestaurantOutline className="w-16 h-16 text-slate-300" />
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
          </div>

          <button
            onClick={onClose}
            aria-label={L.closeButton}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/90 hover:bg-white shadow-md flex items-center justify-center text-slate-700 transition-colors"
          >
            <HiXMark className="w-5 h-5" />
          </button>

          <div className="absolute bottom-0 left-0 right-0 p-5">
            <h2 className="text-2xl sm:text-3xl font-bold text-white drop-shadow-md mb-3">
              {recipe.title || recipe.name || L.noTitle}
            </h2>
            <div className="flex flex-wrap items-center gap-2">
              {(recipe.average_rating > 0 ||
                recipe.favorites_count != null ||
                recipe.comments_count != null) && (
                <>
                  {recipe.average_rating > 0 && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-500/90 text-white text-xs font-medium">
                      {"★".repeat(
                        Math.min(5, Math.round(Number(recipe.average_rating))),
                      )}
                      {"☆".repeat(
                        5 -
                          Math.min(
                            5,
                            Math.round(Number(recipe.average_rating)),
                          ),
                      )}
                      <span className="ml-0.5">
                        {Number(recipe.average_rating).toFixed(1)}
                      </span>
                    </span>
                  )}
                  {recipe.favorites_count != null && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-500/80 text-white text-xs font-medium">
                      <HiHeart className="w-3.5 h-3.5" />
                      {recipe.favorites_count}
                    </span>
                  )}
                  {recipe.comments_count != null && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-600/80 text-white text-xs font-medium">
                      <FiMessageCircle className="w-3.5 h-3.5" />
                      {recipe.comments_count} {L.reviews}
                    </span>
                  )}
                </>
              )}
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-500/80 text-white text-xs font-medium">
                <FiClock className="w-3.5 h-3.5" />
                {recipe.prep_time || 0} {L.minutes}
              </span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-orange-500/80 text-white text-xs font-medium">
                <HiFire className="w-3.5 h-3.5" />
                {recipe.cook_time || 0} {L.minutes}
              </span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/80 text-white text-xs font-medium">
                <FiUsers className="w-3.5 h-3.5" />
                {recipe.servings || 0} {L.persShort}
              </span>
              <span
                className={`px-2.5 py-1 rounded-full text-xs font-semibold text-white ${
                  recipe.difficulty === "Facile"
                    ? "bg-emerald-500"
                    : recipe.difficulty === "Moyen"
                      ? "bg-amber-500"
                      : "bg-orange-500"
                }`}
              >
                {recipe.difficulty === "Facile"
                  ? L.easy
                  : recipe.difficulty === "Moyen"
                    ? L.medium
                    : recipe.difficulty === "Difficile"
                      ? L.hard
                      : recipe.difficulty}
              </span>
            </div>
          </div>
        </div>

        {/* Barre d'actions - avec couleurs */}
        <div className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-orange-50/80 to-amber-50/80 border-b border-orange-100 flex-shrink-0">
          <button
            onClick={handleToggleFavorite}
            disabled={loading}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              isFavorite
                ? "bg-red-500 text-white hover:bg-red-600 shadow-sm"
                : "bg-white text-slate-600 hover:bg-orange-50 border border-slate-200 hover:border-orange-200"
            } ${loading ? "opacity-60 cursor-not-allowed" : ""}`}
          >
            {loading ? (
              <FiClock className="w-4 h-4 animate-spin" />
            ) : isFavorite ? (
              <HiHeart className="w-4 h-4 fill-current" />
            ) : (
              <HiOutlineHeart className="w-4 h-4" />
            )}
            <span>{isFavorite ? L.favoriteSingular : L.favorites}</span>
          </button>
          {onEdit && (
            <button
              onClick={handleEdit}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-amber-700 bg-white border border-amber-200 hover:bg-amber-50 transition-colors"
            >
              <FiEdit3 className="w-4 h-4" />
              {L.edit}
            </button>
          )}
          {onDelete && (
            <button
              onClick={handleDelete}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-red-600 bg-white border border-red-200 hover:bg-red-50 transition-colors ml-auto"
            >
              <FiTrash2 className="w-4 h-4" />
              {L.delete}
            </button>
          )}
        </div>

        {/* Contenu scrollable */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {isTranslatingContent && (
            <div className="px-5 py-2 flex items-center gap-2 text-orange-600 text-sm">
              <span className="animate-spin">🌐</span>
              {L.translating}
            </div>
          )}
          {recipe.description && (
            <section className="p-5 border-b border-slate-100 bg-gradient-to-b from-orange-50/30 to-transparent">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-orange-800 uppercase tracking-wide mb-3">
                <span className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
                  <MdOutlineDescription className="w-4 h-4 text-orange-600" />
                </span>
                {L.descriptionLabel}
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                {translatedContent.description != null
                  ? translatedContent.description
                  : recipe.description}
              </p>
            </section>
          )}

          {recipe.video_url && (
            <section className="p-5 border-b border-slate-100 bg-gradient-to-b from-violet-50/30 to-transparent">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-violet-800 uppercase tracking-wide mb-3">
                <span className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
                  <MdOutlineVideoLibrary className="w-4 h-4 text-violet-600" />
                </span>
                {L.videoLabel}
              </h3>
              <div className="flex items-center gap-3 p-3 bg-white rounded-xl border border-violet-100">
                <p className="flex-1 text-slate-600 text-sm truncate font-mono">
                  {recipe.video_url}
                </p>
                <button
                  onClick={() => window.open(recipe.video_url, "_blank")}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-violet-500 to-purple-500 text-white text-sm font-medium hover:from-violet-600 hover:to-purple-600 transition-all flex-shrink-0 shadow-sm"
                >
                  <FiPlay className="w-4 h-4" />
                  {L.openButton}
                </button>
              </div>
            </section>
          )}

          {recipe.ingredients && (
            <section className="p-5 border-b border-slate-100 bg-gradient-to-b from-emerald-50/30 to-transparent">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-emerald-800 uppercase tracking-wide mb-3">
                <span className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <LuList className="w-4 h-4 text-emerald-600" />
                </span>
                {L.ingredients}
              </h3>
              <ul className="space-y-2">
                {(Array.isArray(translatedContent.ingredients)
                  ? translatedContent.ingredients
                  : recipe.ingredients.split("\n").filter((line) => line.trim())
                ).map((line, i) => (
                  <li
                    key={i}
                    className="flex items-center gap-3 text-slate-700 text-sm"
                  >
                    <span className="w-5 h-5 rounded-full bg-emerald-200 flex items-center justify-center flex-shrink-0 text-emerald-700 text-xs font-medium">
                      ✓
                    </span>
                    {typeof line === "string" ? line.trim() : line}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {recipe.instructions && (
            <section className="p-5 border-b border-slate-100 bg-gradient-to-b from-amber-50/30 to-transparent">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-amber-800 uppercase tracking-wide mb-3">
                <span className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                  <LuChefHat className="w-4 h-4 text-amber-600" />
                </span>
                {L.stepsLabel}
              </h3>
              <ol className="space-y-3">
                {(Array.isArray(translatedContent.instructions)
                  ? translatedContent.instructions
                  : recipe.instructions
                      .split("\n")
                      .filter((line) => line.trim())
                ).map((line, i) => (
                  <li
                    key={i}
                    className="flex gap-3 text-slate-700 text-sm leading-relaxed"
                  >
                    <span className="w-6 h-6 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 text-white flex items-center justify-center flex-shrink-0 text-xs font-semibold shadow-sm">
                      {i + 1}
                    </span>
                    <span className="pt-0.5">
                      {typeof line === "string" ? line.trim() : line}
                    </span>
                  </li>
                ))}
              </ol>
            </section>
          )}

          <section className="p-5 bg-gradient-to-b from-slate-50/50 to-transparent">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800 uppercase tracking-wide mb-3">
              <span className="w-8 h-8 rounded-lg bg-slate-200 flex items-center justify-center">
                <FiInfo className="w-4 h-4 text-slate-600" />
              </span>
              {L.infoLabel}
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="p-3 rounded-xl bg-blue-50 border border-blue-100 text-center">
                <div className="text-xs text-blue-600 font-medium mb-0.5">
                  {L.preparation}
                </div>
                <div className="text-lg font-bold text-blue-900">
                  {recipe.prep_time ?? 0} {L.minutes}
                </div>
              </div>
              <div className="p-3 rounded-xl bg-orange-50 border border-orange-100 text-center">
                <div className="text-xs text-orange-600 font-medium mb-0.5">
                  {L.cooking}
                </div>
                <div className="text-lg font-bold text-orange-900">
                  {recipe.cook_time ?? 0} {L.minutes}
                </div>
              </div>
              <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-100 text-center">
                <div className="text-xs text-emerald-600 font-medium mb-0.5">
                  {L.servings}
                </div>
                <div className="text-lg font-bold text-emerald-900">
                  {recipe.servings ?? 0}
                </div>
              </div>
              <div
                className={`p-3 rounded-xl text-center border ${
                  recipe.difficulty === "Facile"
                    ? "bg-emerald-50 border-emerald-100"
                    : recipe.difficulty === "Moyen"
                      ? "bg-amber-50 border-amber-100"
                      : "bg-orange-50 border-orange-100"
                }`}
              >
                <div
                  className={`text-xs font-medium mb-0.5 ${
                    recipe.difficulty === "Facile"
                      ? "text-emerald-600"
                      : recipe.difficulty === "Moyen"
                        ? "text-amber-600"
                        : "text-orange-600"
                  }`}
                >
                  {L.difficultyLabel}
                </div>
                <div
                  className={`text-lg font-bold ${
                    recipe.difficulty === "Facile"
                      ? "text-emerald-900"
                      : recipe.difficulty === "Moyen"
                        ? "text-amber-900"
                        : "text-orange-900"
                  }`}
                >
                  {recipe.difficulty === "Facile"
                    ? L.easy
                    : recipe.difficulty === "Moyen"
                      ? L.medium
                      : recipe.difficulty === "Difficile"
                        ? L.hard
                        : recipe.difficulty || "—"}
                </div>
              </div>
            </div>
          </section>

          {/* Commentaires */}
          <section className="p-5 border-t border-slate-100 bg-gradient-to-b from-slate-50/50 to-transparent">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800 uppercase tracking-wide mb-3">
              <FiMessageCircle className="w-4 h-4 text-slate-600" />
              {L.comments} ({comments.length})
            </h3>
            {commentsLoading ? (
              <p className="text-slate-500 text-sm">{L.loadingComments}</p>
            ) : comments.length === 0 ? (
              <p className="text-slate-500 text-sm">{L.noCommentsYet}</p>
            ) : (
              <ul className="space-y-3">
                {comments.map((c) => (
                  <li
                    key={c.id}
                    className="p-3 rounded-xl bg-white border border-slate-100 shadow-sm"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-slate-800 text-sm">
                        {c.first_name && c.last_name
                          ? `${c.first_name} ${c.last_name}`
                          : c.username || L.anonymous}
                      </span>
                      <span className="text-xs text-slate-400">
                        {c.created_at
                          ? new Date(c.created_at).toLocaleDateString("fr-FR", {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            })
                          : ""}
                      </span>
                      {c.rating > 0 && (
                        <span className="text-amber-500 text-xs">
                          {"★".repeat(Math.min(5, Math.round(c.rating)))}
                          {"☆".repeat(5 - Math.min(5, Math.round(c.rating)))}
                        </span>
                      )}
                    </div>
                    <p className="text-slate-600 text-sm">
                      {translatedContent.commentTexts &&
                      translatedContent.commentTexts[c.id] !== undefined
                        ? translatedContent.commentTexts[c.id]
                        : (c.comment_text ?? c.text ?? c.content ?? "—")}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-slate-100 to-slate-50 border-t border-slate-200 flex-shrink-0">
          <span className="text-xs text-slate-500 flex items-center gap-1.5">
            <IoTimeOutline className="w-4 h-4 text-slate-400" />
            {L.createdOn}{" "}
            {recipe.created_at
              ? new Date(recipe.created_at).toLocaleDateString("fr-FR", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })
              : "—"}
          </span>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-gradient-to-r from-orange-500 to-amber-500 text-white text-sm font-medium hover:from-orange-600 hover:to-amber-600 transition-all shadow-sm"
          >
            {L.closeButton}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecipePopup;
